/**
 * # Dropdown
 *
 * A dropdown component that displays a trigger element and a content element when triggered.
 * The component also exposes the `Dropdown.Tray` if your use case doesn't align with the default `trigger` element.
 *
 * ## Props
 *
 * - `children` (node): The content to be displayed inside the dropdown.
 * - `className` (string): Additional CSS class name for the dropdown.
 * - `fillWrapper` (bool): Whether the dropdown should fill its wrapper element.
 * - `freeze` (bool): Whether the dropdown should freeze in its open state.
 * - `onDismiss` (func): Callback function to be called when the dropdown is dismissed.
 * - `trigger` (node, required): The trigger element that will toggle the dropdown.
 * - `position` (string): The position of the dropdown relative to the trigger element. (extends `positionTypes`)
 * - `positionFixed` (bool): Whether the dropdown should have a fixed position.
 * - `withMaxHeight` (bool): Whether the dropdown should have a maximum height.
 * - `withMinWidth` (bool): Whether the dropdown should have a minimum width.
 * - `wrapperClassName` (string): Additional CSS class name for the dropdown wrapper.
 * - `trayClassName` (string): Additional CSS class name for the dropdown tray.
 * - `contentClassName` (string): Additional CSS class name for the dropdown content.
 *
 * ## Usage
 *
 * ```jsx
 * import Dropdown from './Dropdown.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <Dropdown
 *       trigger={<button>Toggle Dropdown</button>}
 *       position="bottom-start"
 *       withMinWidth={true}
 *       withMaxHeight={false}
 *     >
 *       <div>Dropdown Content</div>
 *     </Dropdown>
 *   );
 * };
 * ```
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {Manager, Reference, Popper} from 'react-popper';
import makeConstants from 'lib/makeConstants';

import {placements, toModifierArray} from '../../helpers/popper/popperCompat';
import WithToggle from '../../helpers/WithToggle/WithToggle.react';
import ToggleStateNotifier from '../../helpers/WithToggle/ToggleStateNotifier.react';
import IconButton from '../IconButton/IconButton.react';
import {getCardClassName} from '../../utils/getCardClassName';
import {WindowSizeConsumer} from '../../helpers/WindowSizeProvider/WindowSizeProvider.react';
import './dropdown.scss';

const positionTypes = makeConstants(...placements);

type DropdownPosition = (typeof placements)[number];

type DropdownChildrenRenderProp = (args: {
  close: (e?: React.SyntheticEvent) => void;
}) => React.ReactNode;

interface DropdownTrayProps {
  className?: string;
  children?: React.ReactNode;
  position?: DropdownPosition;
  /** react-popper's name for `position`; takes precedence, matching v1's spread order. */
  placement?: DropdownPosition;
  positionFixed?: boolean;
  /** Reference element to anchor to — required when Tray is used standalone (outside Dropdown's Manager). */
  target?: HTMLElement | null;
  modifiers?: Record<string, unknown>;
  style?: React.CSSProperties;
  [key: string]: unknown;
}

interface DropdownProps {
  children?: React.ReactNode | DropdownChildrenRenderProp;
  className?: string;
  fillWrapper?: boolean;
  freeze?: boolean;
  onDismiss?: (e?: React.SyntheticEvent | Event) => void;
  trigger: React.ReactNode;
  position?: DropdownPosition;
  positionFixed?: boolean;
  withMaxHeight?: boolean;
  withMinWidth?: boolean;
  wrapperClassName?: string;
  contentClassName?: string;
  trayClassName?: string;
  onToggleCallback?: (on: boolean) => void;
  closeOnItemClicked?: boolean;
  applyBorderBottomToChildren?: boolean;
  trayProps?: Record<string, unknown>;
  [key: string]: unknown;
}

const modifiers = {
  preventOverflow: {
    padding: 64
  },
  flip: {
    padding: 16
  },
  setMobile: {
    enabled: false,
    order: 840,
    fn: (data) => {
      data.styles.transform = 'none';
      data.styles.top = 'auto';
      data.offsets.popper.position = 'fixed';
      return data;
    }
  }
};

// prevents default click events from bubbling when within another clickable element
function stopClickDefault(e, callback) {
  e.preventDefault();
  callback(e);
}

interface DropdownComponent extends React.FC<DropdownProps> {
  Tray: React.FC<DropdownTrayProps>;
}

const Dropdown: DropdownComponent = ({
  children,
  className,
  fillWrapper,
  freeze,
  onDismiss,
  trigger,
  position = 'bottom-start',
  positionFixed,
  withMaxHeight = false,
  withMinWidth = true,
  wrapperClassName,
  contentClassName,
  trayClassName,
  onToggleCallback = () => {},
  closeOnItemClicked = false,
  applyBorderBottomToChildren = true,
  trayProps = {},
  ...restProps
}) => {
  // Esc key closes dropdown element
  const handleKeyPress = (key, on, onClick) => {
    if (on && key === 'Escape') {
      onClick();
    }
  };
  return (
    <WithToggle freeze={freeze} className={wrapperClassName} onDismiss={onDismiss}>
      {({onClick: onToggle, on}) => {
        const onClick =
          on && typeof onDismiss === 'function'
            ? (e) => {
                e.persist();
                onDismiss(e);
                onToggle(e);
              }
            : onToggle;
        const handleItemClick = (e, childOnClick) => {
          if (childOnClick) {
            childOnClick(e);
          }
          if (closeOnItemClicked) {
            onClick(e);
          }
        };
        const renderDropdownChildren = (
          childrenToRender: React.ReactNode,
          onClickHandler: (e?: React.SyntheticEvent) => void
        ): React.ReactElement<any>[] =>
          React.Children.toArray(childrenToRender).map((rawChild, i, arr) => {
            const child = rawChild as React.ReactElement<any>;
            const childOnClick = child.props.onClick;
            return React.cloneElement(child, {
              className: classnames(child.props.className, {
                'm-dropdown__content--border-bottom':
                  applyBorderBottomToChildren && i + 1 !== arr.length
              }),
              tabIndex: '0',
              onKeyDown: (e) => handleKeyPress(e.key, on, onClickHandler),
              onClick: (e) => {
                e.stopPropagation();
                handleItemClick(e, childOnClick);
              }
            });
          });
        return (
          <>
            <ToggleStateNotifier on={on} onToggleCallback={onToggleCallback} />
            <WindowSizeConsumer>
              {(context) => {
                if (!context) {
                  return null;
                }
                const {viewportWidth, breakpoints} = context;
                modifiers.setMobile.enabled = viewportWidth <= breakpoints.s;
                return (
                  <div className={classnames('m-dropdown', className)} {...restProps}>
                    <Manager>
                      <Reference>
                        {({ref}) => (
                          <div
                            ref={ref}
                            className={classnames('m-dropdown__target', {
                              'm-dropdown__target--fill': fillWrapper
                            })}
                          >
                            {React.Children.toArray(trigger).map((rawEl) => {
                              const el = rawEl as React.ReactElement<any>;
                              // @todo This ternary re-enables attaching events to the trigger element
                              // but it suggests that this component is due for a refactor to make its parts
                              // more composable. @see also Dropdown.Tray
                              const handleClick = el.props.onClick
                                ? (e) => {
                                    e.persist();
                                    el.props.onClick(e);
                                    stopClickDefault(e, onClick);
                                  }
                                : (e) => stopClickDefault(e, onClick);
                              return React.cloneElement(el, {
                                onClick: handleClick,
                                onKeyDown: el.props.onKeyPress
                                  ? (e) => {
                                      e.persist();
                                      el.props.onKeyPress(e);
                                      handleKeyPress(e.key, on, onClick);
                                    }
                                  : (e) => handleKeyPress(e.key, on, onClick),
                                'aria-haspopup': true,
                                'aria-expanded': on
                              });
                            })}
                          </div>
                        )}
                      </Reference>
                      {on && (
                        <Dropdown.Tray
                          className={classnames(trayClassName, 'm-dropdown__content-wrapper', {
                            'm-dropdown__content-wrapper--show': on
                          })}
                          position={position}
                          positionFixed={positionFixed}
                          {...trayProps}
                        >
                          {viewportWidth <= breakpoints.s ? (
                            <IconButton
                              className='m-dropdown__mobile-close'
                              icon='times'
                              variant='outlined'
                              label='close'
                              onClick={onClick}
                            />
                          ) : null}
                          <div
                            role='dialog'
                            onClick={onClick}
                            className={classnames(
                              'm-dropdown__content',
                              contentClassName,
                              {
                                'm-dropdown__content--min': withMinWidth,
                                'm-dropdown__content--max': withMaxHeight
                              },
                              getCardClassName({paddingLevel: 0})
                            )}
                          >
                            {typeof children === 'function'
                              ? renderDropdownChildren(
                                  children({
                                    close: onToggle
                                  }),
                                  onClick
                                )
                              : renderDropdownChildren(children, onClick)}
                          </div>
                        </Dropdown.Tray>
                      )}
                    </Manager>
                  </div>
                );
              }}
            </WindowSizeConsumer>
          </>
        );
      }}
    </WithToggle>
  );
};

// Standalone (Manager-less) Tray call sites pass the anchor as `target` and the
// position as `placement` — both reached Popper v1 through the `...rest` spread,
// so they must be forwarded explicitly now that rest lands on the wrapper div.
Dropdown.Tray = ({
  className,
  children,
  position,
  placement,
  positionFixed,
  target,
  modifiers: trayPropsModifiers,
  style,
  ...rest
}) => (
  <Popper
    modifiers={toModifierArray({...modifiers, ...trayPropsModifiers})}
    placement={placement ?? position}
    referenceElement={target ?? undefined}
    strategy={positionFixed ? 'fixed' : 'absolute'}
  >
    {({ref, style: popperStyle, placement: computedPlacement}) => (
      <div
        ref={ref}
        style={{...popperStyle, ...(style as React.CSSProperties)}}
        data-placement={computedPlacement}
        className={className}
        {...rest}
      >
        {children}
      </div>
    )}
  </Popper>
);

Dropdown.Tray.propTypes = {
  children: PropTypes.node as PropTypes.Validator<React.ReactNode>,
  className: PropTypes.string,
  position: PropTypes.oneOf(Object.values(positionTypes)),
  placement: PropTypes.oneOf(Object.values(positionTypes)),
  positionFixed: PropTypes.bool,
  target: PropTypes.instanceOf(Element) as PropTypes.Requireable<HTMLElement>,
  modifiers: PropTypes.object as PropTypes.Requireable<Record<string, unknown>>
};

Dropdown.propTypes = {
  children: PropTypes.node as PropTypes.Validator<React.ReactNode | DropdownChildrenRenderProp>,
  className: PropTypes.string,
  fillWrapper: PropTypes.bool,
  freeze: PropTypes.bool,
  onDismiss: PropTypes.func,
  position: PropTypes.oneOf(Object.values(positionTypes)),
  positionFixed: PropTypes.bool,
  trigger: PropTypes.node.isRequired as PropTypes.Validator<React.ReactNode>,
  withMaxHeight: PropTypes.bool,
  withMinWidth: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  trayClassName: PropTypes.string,
  onToggleCallback: PropTypes.func,
  closeOnItemClicked: PropTypes.bool,
  trayProps: PropTypes.object as PropTypes.Requireable<Record<string, unknown>>,
  applyBorderBottomToChildren: PropTypes.bool,
  contentClassName: PropTypes.string
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
